import {Component, computed, inject, input} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import {
  BicepsFlexedIcon,
  Calendar1Icon,
  CalendarIcon,
  CalendarSyncIcon,
  HandPlatterIcon,
  LightbulbIcon,
  LucideAngularModule,
  MoonIcon,
  ScanFaceIcon,
  SquareActivityIcon,
  SunIcon,
  SunriseIcon,
  SunsetIcon
} from 'lucide-angular';
import {PersonGenderIconComponent} from '@domain/common/component/person-gender-icon/person-gender-icon.component';
import {IntegerRangeComponent} from '@shared/field/integer-range/integer-range.component';
import {ActivityTypeIconComponent} from '@domain/activity/component/activity-type-icon/activity-type-icon.component';
import {PlacePipe} from '@domain/place/pipe/place.pipe';
import {
  ActivitySearchService
} from '@public/activity/search-activity/components/activity-search-filters/activity-search.service';
import {DayTime, PersonGender, WeekDay} from '@domain/common/common';
import {TimeUtil} from '@shared/util/time.util';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivityScheduleLevel} from '@domain/activity/schedule/schedule.dto';
import {ActivityType} from '@domain/activity/activity.dto';
import {RouteGenerator} from '@core/route-generator';
import {UI_ROUTES} from '@core/ui.routes';
import {
  ScheduleLevelIconComponent
} from '@domain/activity/schedule/component/schedule-level-icon/schedule-level-icon.component';

@Component({
  selector: 'app-activity-search-filters',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    LucideAngularModule,
    IntegerRangeComponent,
    PersonGenderIconComponent,
    ActivityTypeIconComponent,
    ScheduleLevelIconComponent
  ],
  providers: [PlacePipe],
  templateUrl: './activity-search-filters.component.html',
  styleUrl: './activity-search-filters.component.scss'
})
export class ActivitySearchFiltersComponent {

  public readonly autoSearch = input(false);

  private readonly searchService = inject(ActivitySearchService);
  private readonly transloco = inject(TranslocoService);

  public readonly routes = {
    home: RouteGenerator.abs(UI_ROUTES.public.dashboard)
  };

  public readonly icons = {
    activity: SquareActivityIcon,
    schedule: CalendarIcon,
    audience: ScanFaceIcon,
    [DayTime.Morning]: SunriseIcon,
    [DayTime.Noon]: HandPlatterIcon,
    [DayTime.Afternoon]: SunIcon,
    [DayTime.Evening]: SunsetIcon,
    [DayTime.Night]: MoonIcon,
    level: BicepsFlexedIcon,
    repeat0: Calendar1Icon,
    repeat1: CalendarSyncIcon,
    info: LightbulbIcon
  };

  public readonly dayTimeMins = TimeUtil.DAYTIME_MIN;
  public readonly dayTimeMaxs = TimeUtil.DAYTIME_MAX;
  public selectedDays = toSignal(this.searchService.selectedDays$, {initialValue: [] as WeekDay[]});
  public selectedTimes = toSignal(this.searchService.selectedDayTimes$, {initialValue: [] as DayTime[]});
  public selectedLevels = toSignal(this.searchService.selectedLevels$, {initialValue: [] as ActivityScheduleLevel[]});
  public selectedGenders = toSignal(this.searchService.selectedGenders$, {initialValue: [] as PersonGender[]});
  public selectedActivityTypes = toSignal(this.searchService.selectedActivityTypes$, {initialValue: [] as ActivityType[]});
  public selectedIsRecurring = toSignal(this.searchService.selectedIsRecurring$, {initialValue: [] as number[]});

  public readonly selectedLevelsString = computed(
    () => this.selectedLevels()?.map(l => this.transloco.translate('guest.search.Level.' + l)).join(', ') ?? null
  );

  public readonly selectedGendersString = computed(
    () => this.selectedGenders()?.map(g => this.transloco.translate('guest.search.Gender.' + g)).join(', ') ?? null
  );

  public readonly selectedActivityTypesString = computed(
    () => this.selectedActivityTypes()?.map(t => this.transloco.translate('guest.search.ActivityType.' + t)).join(', ') ?? null
  );

  public readonly weekDays = Object.values(WeekDay) as WeekDay[];
  public readonly levels = Object.values(ActivityScheduleLevel) as ActivityScheduleLevel[];
  public readonly genders = Object.values(PersonGender) as PersonGender[];
  public readonly activityTypes = ActivityType;
  public readonly dayTimes = Object.values(DayTime) as DayTime[];
  public readonly filterForm = this.searchService.filterForm;

  public resetFilters(): void {
    this.searchService.resetFilters();
  }

  public toggleDay(day: string) {
    this.searchService.toggleDay(day);
  }

  public toggleLevel(level: string) {
    this.searchService.toggleLevel(level);
  }

  public toggleGender(gender: string) {
    this.searchService.toggleGender(gender);
  }

  public toggleActivityType(activityType: string) {
    this.searchService.toggleActivityType(activityType);
  }

  public toggleDayTime(time: string) {
    this.searchService.toggleDayTime(time);
  }

  public toggleIsRecurring(isRecurring: number) {
    this.searchService.toggleIsRecurring(isRecurring);
  }

}
